import { KYC_DOCUMENT_REQUIREMENTS, KYC_INPUT_TYPE } from '../config';
import { detectFileTypeFromContent } from './file';

const getDocumentRequirements = (kycType = '') => {
  return KYC_DOCUMENT_REQUIREMENTS[kycType] || [];
};

const getDocumentRequirementCount = (kycType = '') => {
  return getDocumentRequirements(kycType).length;
};

/**
 * Checks if a document is mandatory (defaults to true if isMandatory flag is not present)
 * @param document - Document object from KYC_DOCUMENT_REQUIREMENTS or backend
 * @returns true if document is mandatory, false if optional
 */
const isDocumentMandatory = (document = {}) => {
  return document.isMandatory ?? document.is_mandatory ?? true;
};

/**
 * Gets mandatory documents for a given KYC type
 * @param kycType - The KYC type
 * @returns Array of mandatory documents
 */
const getMandatoryDocuments = (kycType = '') => {
  const documents = getDocumentRequirements(kycType);
  return documents.filter((doc) => isDocumentMandatory(doc));
};

/**
 * Gets optional documents for a given KYC type
 * @param kycType - The KYC type
 * @returns Array of optional documents
 */
const getOptionalDocuments = (kycType = '') => {
  const documents = getDocumentRequirements(kycType);
  return documents.filter((doc) => !isDocumentMandatory(doc));
};

const getKycStatusFromUploads = (uploads = [], totalRequired = 0, kycType = '') => {
  if (!uploads.length) {
    return totalRequired ? 'PENDING' : 'NOT_STARTED';
  }

  // If kycType is provided, separate mandatory and optional uploads
  if (kycType) {
    const documentsForKycType = getDocumentRequirements(kycType);
    const mandatoryDocuments = getMandatoryDocuments(kycType);

    // Filter mandatory uploads (exclude skipped)
    const mandatoryUploads = uploads.filter((upload) => {
      const docConfig = documentsForKycType.find((doc) => doc.id === upload.document_title);
      return docConfig && isDocumentMandatory(docConfig) && upload.status !== 'SKIPPED';
    });

    const mandatoryStatuses = mandatoryUploads.map(({ status }) => status);

    // Check mandatory documents status
    if (mandatoryStatuses.includes('DECLINED')) {
      return 'DECLINED';
    }

    const allMandatoryApproved =
      mandatoryUploads.length === mandatoryDocuments.length &&
      mandatoryStatuses.every((status) => status === 'APPROVED');

    if (allMandatoryApproved) {
      return 'APPROVED';
    }

    if (mandatoryStatuses.includes('PENDING') || mandatoryStatuses.includes('APPROVED')) {
      return 'PENDING';
    }

    if (mandatoryUploads.length < mandatoryDocuments.length) {
      return 'IN_PROGRESS';
    }

    return 'PENDING';
  }

  // Fallback to old behavior if kycType not provided
  const statuses = uploads.map(({ status }) => status);

  if (statuses.includes('DECLINED')) {
    return 'DECLINED';
  }

  const everyApproved = statuses.every((status) => status === 'APPROVED');
  if (everyApproved && (!totalRequired || uploads.length >= totalRequired)) {
    return 'APPROVED';
  }

  if (statuses.includes('PENDING')) {
    return 'PENDING';
  }

  if (totalRequired && uploads.length < totalRequired) {
    return 'IN_PROGRESS';
  }

  return 'PENDING';
};

const formatKycTypeLabel = (kycType) => {
  if (!kycType) return '';
  return kycType
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const mapUploadsToMerchantSummaries = (uploads = []) => {
  const grouped = uploads.reduce((acc, upload) => {
    const merchantId = upload.merchant_id;
    if (!acc[merchantId]) {
      acc[merchantId] = [];
    }

    acc[merchantId].push(upload);
    return acc;
  }, {});

  return Object.values(grouped)
    .map((merchantUploads) => {
      const [firstUpload] = merchantUploads;
      const kycType = firstUpload?.kyc?.type;
      const docsRequired = getDocumentRequirementCount(kycType);
      const status = getKycStatusFromUploads(merchantUploads, docsRequired);
      const latestUpload = merchantUploads.reduce((latest, upload) => {
        if (!latest) return upload;
        return new Date(upload.updated_at) > new Date(latest.updated_at) ? upload : latest;
      }, null);

      const contactEmail =
        firstUpload?.merchant?.merchantUser?.[0]?.user?.email ||
        firstUpload?.merchant?.email ||
        '--';

      const submittedWithinRequirement = merchantUploads.length;
      const documentProgress = docsRequired
        ? `${Math.min(submittedWithinRequirement, docsRequired)}/${docsRequired}`
        : `${submittedWithinRequirement}`;

      return {
        merchant_id: firstUpload?.merchant_id,
        merchant_name: firstUpload?.merchant?.name || 'Unknown Merchant',
        merchant_contact: contactEmail,
        kyc_type: kycType,
        kyc_type_label: formatKycTypeLabel(kycType),
        document_progress: documentProgress,
        docs_submitted: submittedWithinRequirement,
        docs_required: docsRequired,
        status,
        updated_at: latestUpload?.updated_at || firstUpload?.updated_at,
        merchant: firstUpload?.merchant
      };
    })
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
};

const mapKycsToMerchantSummaries = (kycs = []) => {
  return kycs
    .map((kyc) => {
      const kycType = kyc?.type;
      const documentsForKycType = getDocumentRequirements(kycType);
      const mandatoryDocuments = getMandatoryDocuments(kycType);
      const optionalDocuments = getOptionalDocuments(kycType);
      const uploads = kyc?.kycUploads || [];

      // Separate mandatory and optional uploads
      const mandatoryUploads = uploads.filter((upload) => {
        const docConfig = documentsForKycType.find((doc) => doc.id === upload.document_title);
        return docConfig && isDocumentMandatory(docConfig);
      });

      const optionalUploads = uploads.filter((upload) => {
        const docConfig = documentsForKycType.find((doc) => doc.id === upload.document_title);
        return docConfig && !isDocumentMandatory(docConfig);
      });

      const skippedOptionalCount = optionalUploads.filter((u) => u.status === 'SKIPPED').length;
      const status = getKycStatusFromUploads(uploads, mandatoryDocuments.length, kycType);

      const latestUpload = uploads.reduce((latest, upload) => {
        if (!latest) return upload;
        return new Date(upload.updated_at) > new Date(latest.updated_at) ? upload : latest;
      }, null);

      const merchantUser = kyc?.merchant?.merchantUser;
      const merchantUserArray = Array.isArray(merchantUser)
        ? merchantUser
        : merchantUser
        ? [merchantUser]
        : [];
      const contactEmail = merchantUserArray[0]?.user?.email || kyc?.merchant?.email || '--';

      const mandatoryUploadedCount = mandatoryUploads.length;
      const mandatoryRequiredCount = mandatoryDocuments.length;

      // Document progress: show mandatory progress and skipped optional count
      let documentProgress = '';
      if (mandatoryRequiredCount > 0) {
        documentProgress = `${mandatoryUploadedCount}/${mandatoryRequiredCount} mandatory`;
        if (skippedOptionalCount > 0) {
          documentProgress += `, ${skippedOptionalCount} optional skipped`;
        }
      } else {
        documentProgress = `${uploads.length}`;
      }

      return {
        merchant_id: kyc?.merchant_id,
        merchant_name: kyc?.merchant?.name || 'Unknown Merchant',
        merchant_contact: contactEmail,
        kyc_type: kycType,
        kyc_type_label: formatKycTypeLabel(kycType),
        document_progress: documentProgress,
        docs_submitted: uploads.length,
        docs_required: mandatoryRequiredCount,
        status,
        updated_at: latestUpload?.updated_at || kyc?.updated_at,
        merchant: kyc?.merchant,
        kyc: kyc
      };
    })
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
};

const getDocumentInputType = (kycType, documentName) => {
  const documents = KYC_DOCUMENT_REQUIREMENTS[kycType] || [];
  const document = documents.find((doc) => doc.id === documentName);
  return document?.inputType || KYC_INPUT_TYPE.FILE;
};

const downloadDocument = async (doc, getSignedUrlFn) => {
  if (doc.input_type && doc.input_type !== KYC_INPUT_TYPE.FILE) {
    if (doc.value) {
      try {
        await navigator.clipboard.writeText(doc.value);
        return 'copied';
      } catch {
        return false;
      }
    }
    return false;
  }
  if (!doc.document_secure_url) return false;

  try {
    const signedUrl = await getSignedUrlFn(doc.reference);

    if (!signedUrl) {
      throw new Error('Failed to get signed URL');
    }

    const response = await fetch(signedUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const getFileExtension = (url) => {
      const urlPath = url.split('?')[0];
      const match = urlPath.match(/\.([a-zA-Z0-9]+)$/);
      return match ? match[1] : null;
    };

    const getMimeTypeExtension = (mimeType) => {
      const mimeMap = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'image/webp': 'webp',
        'image/bmp': 'bmp'
      };
      return mimeMap[mimeType] || null;
    };

    let fileExtension =
      getFileExtension(signedUrl) ||
      getFileExtension(doc.document_secure_url) ||
      getMimeTypeExtension(blob.type);

    if (!fileExtension || blob.type === 'application/octet-stream') {
      fileExtension = await detectFileTypeFromContent(blob);
    }
    let fileName = doc.document_title || 'document';

    if (fileExtension && !fileName.toLowerCase().endsWith(`.${fileExtension.toLowerCase()}`)) {
      fileName = `${fileName}.${fileExtension}`;
    }

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error('Download failed:', error);
    return false;
  }
};

const SUPPORTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const SUPPORTED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

const validateKycFileFormat = (file) => {
  if (!file || !file[0]) {
    return 'Please select a file';
  }

  const selectedFile = file[0];
  const fileExtension = selectedFile.name
    .toLowerCase()
    .substring(selectedFile.name.lastIndexOf('.'));
  const fileType = selectedFile.type;

  const isValidExtension = SUPPORTED_FILE_EXTENSIONS.includes(fileExtension);
  const isValidMimeType = SUPPORTED_FILE_TYPES.includes(fileType.toLowerCase());

  if (!isValidExtension && !isValidMimeType) {
    return `Unsupported file format. Please upload a PDF, JPG, or PNG file.`;
  }

  const maxFileSize = 10 * 1024 * 1024;
  if (selectedFile.size > maxFileSize) {
    return 'File size exceeds 10MB limit. Please upload a smaller file.';
  }

  return true;
};

export {
  getDocumentRequirementCount,
  getDocumentRequirements,
  getKycStatusFromUploads,
  mapUploadsToMerchantSummaries,
  mapKycsToMerchantSummaries,
  formatKycTypeLabel,
  downloadDocument,
  validateKycFileFormat,
  isDocumentMandatory,
  getMandatoryDocuments,
  getOptionalDocuments,
  getDocumentInputType
};
